import React, { FC, useCallback } from 'react';
import DataTable from '@salesforce/design-system-react/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/components/data-table/cell';
import ExpenseInventoryIdCell from './ExpenseInventoryIdCell';
import ExpenseInventoryStatusCell from './ExpenseInventoryStatusCell';
import SubmitDateCell from '../../InventoryRequestDataTable/SubmitDateCell';
import AccountCell from './AccountCell';
import SalesRepNameCell from './SalesRepNameCell';
import { LIMIT, EXPENSE_INVENTORY_SCREEN_TYPE } from '../../../../util/constants';

interface ExpenseInventoryOrder {
  id: string;
  caseExternalId: string;
  caseNumber: string;
  status: string;
  accountName: string;
  accountDisplay?: string;
  salesRepName: string;
  salesRepHoldReason?: string | null;
  submittedDate: string;
  orderType: string;
  failureReason?: string;
  erpOrderNumber?: string;
  writeOffType?: string;
}

interface Props {
  type: string;
  status: string;
  orders: ExpenseInventoryOrder[];
  getRequestsList: (options: { variables: Record<string, unknown> }) => void;
  onSortChange: (column: ColumnSort) => void;
  activeSort?: ColumnSort;
  filters: Record<string, unknown> | null;
  offsetData?: number;
}

export interface ColumnSort {
  property: string;
  sortDirection: SortDirection;
}

type SortDirection = 'asc' | 'desc';

const COLUMN_PROPERTY = {
  CASE_NUMBER: 'caseNumber',
  ORDER_TYPE: 'orderType',
  STATUS: 'status',
  ACCOUNT: 'accountName',
  SALES_REP_NAME: 'salesRepName',
  SALES_REP_HOLD_REASON: 'salesRepHoldReason',
  SUBMITTED_DATE: 'submittedDate',
  FAILURE_REASON: 'failureReason',
  ERP_ORDER_NUMBER: 'erpOrderNumber',
  WRITE_OFF_TYPE: 'writeOffType',
} as const;

// CustomDataTableCell must have the same displayName as DataTableCell or it will be ignored.
ExpenseInventoryIdCell.displayName = DataTableCell.displayName;
ExpenseInventoryStatusCell.displayName = DataTableCell.displayName;
SubmitDateCell.displayName = DataTableCell.displayName;
AccountCell.displayName = DataTableCell.displayName;
SalesRepNameCell.displayName = DataTableCell.displayName;

const ExpenseInventoryDataTable: FC<Props> = ({
  orders,
  type,
  status,
  getRequestsList,
  onSortChange,
  activeSort,
  filters,
  offsetData,
}) => {
  const handleSort = useCallback(
    (sortColumn: ColumnSort): void => {
      const { property: sortProperty, sortDirection } = sortColumn;
      onSortChange(sortColumn);
      getRequestsList({
        variables: {
          limit: LIMIT,
          orderBy: sortProperty,
          orderSortType: sortDirection,
          offset: offsetData,
          type: status,
          filters,
        },
      });
    },
    [status, offsetData, filters, getRequestsList, onSortChange]
  );

  const columnSortProps = (
    property: string
  ): { isSorted: boolean; sortDirection: SortDirection | undefined } => ({
    isSorted: activeSort?.property === property,
    sortDirection: activeSort?.property === property ? activeSort.sortDirection : undefined,
  });

  return (
    <DataTable
      assistiveText={{
        columnSort: 'sort this column',
        columnSortedAscending: 'asc',
        columnSortedDescending: 'desc',
      }}
      items={orders}
      id="Expense-Inventory-DataTable"
      className="slds-table_bordered font-12"
      fixedLayout
      keyboardNavigation
      joined
      onSort={handleSort}
    >
      {[
        <DataTableColumn
          key={COLUMN_PROPERTY.CASE_NUMBER}
          label="Expense"
          property={COLUMN_PROPERTY.CASE_NUMBER}
          width="12%"
          sortable
          {...columnSortProps(COLUMN_PROPERTY.CASE_NUMBER)}
        >
          <ExpenseInventoryIdCell type={type} />
        </DataTableColumn>,
        status !== EXPENSE_INVENTORY_SCREEN_TYPE.OPEN && (
          <DataTableColumn
            key={COLUMN_PROPERTY.ORDER_TYPE}
            label="Order Type"
            property={COLUMN_PROPERTY.ORDER_TYPE}
            width="10%"
            sortable
            {...columnSortProps(COLUMN_PROPERTY.ORDER_TYPE)}
          />
        ),
        <DataTableColumn
          key={COLUMN_PROPERTY.WRITE_OFF_TYPE}
          label="Write Off Type"
          property={COLUMN_PROPERTY.WRITE_OFF_TYPE}
          width="10%"
          sortable
          {...columnSortProps(COLUMN_PROPERTY.WRITE_OFF_TYPE)}
        />,
        <DataTableColumn
          key={COLUMN_PROPERTY.STATUS}
          label="Status"
          property={COLUMN_PROPERTY.STATUS}
          width="12%"
          sortable
          {...columnSortProps(COLUMN_PROPERTY.STATUS)}
        >
          <ExpenseInventoryStatusCell />
        </DataTableColumn>,
        status === EXPENSE_INVENTORY_SCREEN_TYPE.PROCESSED && (
          <DataTableColumn
            key={COLUMN_PROPERTY.ERP_ORDER_NUMBER}
            label="ERP Order #"
            property={COLUMN_PROPERTY.ERP_ORDER_NUMBER}
            width="10%"
            sortable
            {...columnSortProps(COLUMN_PROPERTY.ERP_ORDER_NUMBER)}
          />
        ),
        <DataTableColumn
          key={COLUMN_PROPERTY.SALES_REP_NAME}
          label="Sales Rep"
          property={COLUMN_PROPERTY.SALES_REP_NAME}
          sortable
          {...columnSortProps(COLUMN_PROPERTY.SALES_REP_NAME)}
        >
          <SalesRepNameCell />
        </DataTableColumn>,
        <DataTableColumn
          key={COLUMN_PROPERTY.ACCOUNT}
          label="Account"
          property={COLUMN_PROPERTY.ACCOUNT}
          sortable
          {...columnSortProps(COLUMN_PROPERTY.ACCOUNT)}
        >
          <AccountCell />
        </DataTableColumn>,
        <DataTableColumn
          key={COLUMN_PROPERTY.SALES_REP_HOLD_REASON}
          label="Hold Reason"
          property={COLUMN_PROPERTY.SALES_REP_HOLD_REASON}
          width="10%"
          sortable
          {...columnSortProps(COLUMN_PROPERTY.SALES_REP_HOLD_REASON)}
        />,
        <DataTableColumn
          key={COLUMN_PROPERTY.SUBMITTED_DATE}
          label="Submit Date"
          property={COLUMN_PROPERTY.SUBMITTED_DATE}
          width="8%"
          sortable
          {...columnSortProps(COLUMN_PROPERTY.SUBMITTED_DATE)}
        >
          <SubmitDateCell />
        </DataTableColumn>,
        status === EXPENSE_INVENTORY_SCREEN_TYPE.FAILED && (
          <DataTableColumn
            key={COLUMN_PROPERTY.FAILURE_REASON}
            label="Failure Reason"
            property={COLUMN_PROPERTY.FAILURE_REASON}
            sortable
            {...columnSortProps(COLUMN_PROPERTY.FAILURE_REASON)}
          />
        ),
      ]}
    </DataTable>
  );
};

export default React.memo(ExpenseInventoryDataTable);
